import { useCreateDialogLookModal } from 'components/create-dialog/CreateDialogModalFrame';
import { PlusIcon } from 'components/icons';
import { Button } from 'components/ui/button';
import { ReactNode, useCallback } from 'react';
import { ClassifierData } from 'types/classifiers';
import { InvoiceData, InvoiceRowForm } from 'types/invoices';
import { getClassifierText } from 'utils/getTranslations';
import { AdminFeeEditModal } from './AdminFeeEditModal';
import { totalsButtonStyle } from '../TotalsTableCard';

const FORM_OVERFLOW_CUSTOM_STYLE = {
    display: 'grid',
    gridTemplateRows: 'auto 1fr auto',
    width: '700px',
};

interface Props {
    children: ReactNode;
    handleSubmit: (data: InvoiceRowForm) => void;
    invoice: InvoiceData;
    matterId: number;
    baseAmount: number;
    defaultAdminFeeRate?: number;
    classifiers: Record<string, ClassifierData>;
}

const useAdminFeeAddModal = (
    invoice: InvoiceData,
    matterId: number,
    handleSubmit: (data: InvoiceRowForm) => void,
    baseAmount: number,
    classifiers: Record<string, ClassifierData>,
    defaultAdminFeeRate?: number
) => {
    const { openModal, closeModal } = useCreateDialogLookModal();

    const defaultRate = defaultAdminFeeRate ?? 0;
    const defaultTotal = defaultRate ? Math.round(((baseAmount * defaultRate) / 100) * 100) / 100 : 0;
    const invoiceLang = invoice.invoiceTemplate?.primaryLanguageTypeCode ?? 'eng';
    const adminFeeLabel = getClassifierText(classifiers['INVOICE_ROW_TYPE'], 'ADMIN_FEE', undefined, invoiceLang);
    const defaultDescription = defaultRate ? `${adminFeeLabel} ${defaultRate.toFixed(2)}%` : adminFeeLabel;

    const openAddModal = useCallback(() => {
        openModal(
            <AdminFeeEditModal
                onClose={closeModal}
                action={'create'}
                invoice={invoice}
                baseAmount={baseAmount}
                initialData={{
                    invoiceRowId: 0,
                    matterId: matterId,
                    description: defaultDescription,
                    itemTotal: defaultTotal,
                    discountRate: 0,
                    discountAmount: '0',
                    amountBeforeDiscount: baseAmount,
                    adminFeeRate: defaultRate,
                }}
                onSubmit={handleSubmit}
                autoSyncDescription
                adminFeeLabel={adminFeeLabel}
            />,
            closeModal,
            FORM_OVERFLOW_CUSTOM_STYLE
        );
    }, [
        closeModal,
        handleSubmit,
        matterId,
        openModal,
        invoice,
        baseAmount,
        defaultRate,
        defaultTotal,
        defaultDescription,
        adminFeeLabel,
    ]);

    return { openAddModal };
};

export const AdminFeeAddButton = (props: Props) => {
    const { handleSubmit, invoice, matterId, baseAmount, defaultAdminFeeRate, classifiers, children } = props;
    const { openAddModal } = useAdminFeeAddModal(
        invoice,
        matterId,
        handleSubmit,
        baseAmount,
        classifiers,
        defaultAdminFeeRate
    );

    return (
        <Button {...totalsButtonStyle('w-[125px]')} onClick={openAddModal}>
            {/* Classic's label carries its own "+". */}
            <PlusIcon aria-hidden className="new:block hidden size-3.5" />
            {children}
        </Button>
    );
};
